/**
 * Asiento y apunte (spec/03-data-model.md §2). Construccion y validacion de
 * INV-1 (suma cero por moneda), INV-2 (>=2 apuntes), INV-3/INV-5 (identidad
 * estable, inmutabilidad: no hay funcion de edicion, solo construccion y
 * reversion).
 */

import { generateEntryId } from "./ids.js";
import { money, sumByCurrency, isZeroMoney, type Money } from "./money.js";

export type EntryOrigin = "voice" | "manual" | "import";

const VALID_ORIGINS: readonly EntryOrigin[] = ["voice", "manual", "import"];

export interface Posting {
  readonly index: number;
  readonly accountId: string;
  readonly amountMinor: bigint;
  readonly currency: string;
  readonly scale: number;
  readonly quantityMinor: bigint | null;
  readonly instrumentId: string | null;
}

export interface PostingInput {
  accountId: string;
  amountMinor: bigint;
  currency: string;
  scale: number;
  quantityMinor?: bigint | null;
  instrumentId?: string | null;
}

/** Un asiento confirmado. No expone ninguna operacion de mutacion (INV-3): solo se construye o se revierte. */
export interface Entry {
  readonly entryId: string;
  readonly date: string; // fecha civil ISO-8601 YYYY-MM-DD
  readonly description: string;
  readonly origin: EntryOrigin;
  readonly reversesEntryId: string | null;
  readonly postings: readonly Posting[];
}

export class UnbalancedEntryError extends Error {
  constructor(details: string) {
    super(`Asiento descuadrado: ${details}`);
    this.name = "UnbalancedEntryError";
  }
}

export class InsufficientPostingsError extends Error {
  constructor(count: number) {
    super(`Un asiento necesita dos o mas apuntes (INV-2); recibidos: ${count}`);
    this.name = "InsufficientPostingsError";
  }
}

export class InvalidEntryDateError extends Error {
  constructor(date: string) {
    super(`Fecha de asiento invalida (se espera YYYY-MM-DD): ${date}`);
    this.name = "InvalidEntryDateError";
  }
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function assertValidDate(date: string): void {
  if (!ISO_DATE_RE.test(date) || Number.isNaN(Date.parse(date + "T00:00:00Z"))) {
    throw new InvalidEntryDateError(date);
  }
}

function assertValidOrigin(origin: string): asserts origin is EntryOrigin {
  if (!(VALID_ORIGINS as readonly string[]).includes(origin)) {
    throw new RangeError(`Origen invalido: ${origin}. Debe ser uno de: ${VALID_ORIGINS.join(", ")}`);
  }
}

/**
 * Verifica INV-1 (suma cero por moneda) sin construir el asiento.
 * Lanza UnbalancedEntryError si alguna moneda no cuadra.
 */
export function assertBalanced(postings: readonly PostingInput[]): void {
  const totals = sumByCurrency(
    postings.map((p): Money => money(p.amountMinor, p.currency, p.scale)),
  );
  const offenders: string[] = [];
  for (const [currency, total] of totals) {
    if (!isZeroMoney(total)) {
      offenders.push(`${currency}: diferencia ${total.amountMinor.toString()} unidades minimas`);
    }
  }
  if (offenders.length > 0) {
    throw new UnbalancedEntryError(offenders.join("; "));
  }
}

export interface BuildEntryInput {
  date: string;
  description: string;
  origin: EntryOrigin;
  postings: readonly PostingInput[];
  reversesEntryId?: string | null;
  /** Inyectable para tests deterministas; por defecto usa el reloj y crypto reales. */
  entryId?: string;
}

/**
 * Construye y valida un asiento. Unico punto de entrada para crear un Entry:
 * no existe ninguna funcion de edicion posterior (INV-3).
 */
export function buildEntry(input: BuildEntryInput): Entry {
  assertValidDate(input.date);
  assertValidOrigin(input.origin);
  if (input.postings.length < 2) {
    throw new InsufficientPostingsError(input.postings.length);
  }
  assertBalanced(input.postings);

  const postings: Posting[] = input.postings.map((p, index) => ({
    index,
    accountId: p.accountId,
    amountMinor: p.amountMinor,
    currency: p.currency,
    scale: p.scale,
    quantityMinor: p.quantityMinor ?? null,
    instrumentId: p.instrumentId ?? null,
  }));

  return {
    entryId: input.entryId ?? generateEntryId(),
    date: input.date,
    description: input.description,
    origin: input.origin,
    reversesEntryId: input.reversesEntryId ?? null,
    postings,
  };
}

/**
 * Corrige un asiento por reversion (FR-LEDGER-3): nunca edita ni borra el
 * original. El asiento de reversion tiene los mismos apuntes con el signo
 * invertido y referencia al original via reversesEntryId.
 */
export function reverseEntry(
  original: Entry,
  options?: { date?: string; description?: string; origin?: EntryOrigin; entryId?: string },
): Entry {
  const reversedPostings: PostingInput[] = original.postings.map((p) => ({
    accountId: p.accountId,
    amountMinor: -p.amountMinor,
    currency: p.currency,
    scale: p.scale,
    quantityMinor: p.quantityMinor === null ? null : -p.quantityMinor,
    instrumentId: p.instrumentId,
  }));

  return buildEntry({
    date: options?.date ?? original.date,
    description: options?.description ?? `Reversión de: ${original.description}`,
    origin: options?.origin ?? "manual",
    postings: reversedPostings,
    reversesEntryId: original.entryId,
    ...(options?.entryId !== undefined ? { entryId: options.entryId } : {}),
  });
}
